import React, { useState, useEffect } from 'react';
import ToolConfigurationModal from './ToolConfigurationModal';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  PhoneDisabled,
  TransferWithinAStation,
  PhoneForwarded,
  DataObject,
  Dialpad,
  Add as AddIcon,
  Schedule,
  VolumeUp,
  Chat
} from '@mui/icons-material';



// All available capabilities that can be added to the agent
const AVAILABLE_CAPABILITIES = [
  {
    id: 'end_call',
    name: 'End Call',
    description: 'End the current call',
    icon: <PhoneDisabled />,
    category: 'call_control',
    configurable: true
  },
  {
    id: 'transfer_call',
    name: 'Call Transfer',
    description: 'Transfer the call to a human agent',
    icon: <PhoneForwarded />,
    category: 'call_control',
    configurable: true
  },
  {
    id: 'agent_swap',
    name: 'Agent Transfer',
    description: 'Transfer to another agent',
    icon: <TransferWithinAStation />,
    category: 'call_control',
    configurable: true
  },
  {
    id: 'press_digit',
    name: 'Press Digit IVR Navigation',
    description: 'Press DTMF digits for IVR navigation',
    icon: <Dialpad />,
    category: 'call_control',
    configurable: true
  },
  {
    id: 'extract_dynamic_variable',
    name: 'Extract Dynamic Variable',
    description: 'Extract and store information from the conversation',
    icon: <DataObject />,
    category: 'data_processing',
    configurable: true
  }
];

const AgentGoals = ({ 
  formData, 
  onFormDataChange, 
  knowledgeBases = [],
  onKnowledgeBaseChange 
}) => {
  const [configuredActions, setConfiguredActions] = useState(() => {
    // Ensure configuredActions is always an object
    const actions = formData.configuredActions;
    return actions && typeof actions === 'object' ? actions : {};
  });
  const [showToolModal, setShowToolModal] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [editingToolKey, setEditingToolKey] = useState(null); // Track the unique key being edited
  const [showCapabilityDropdown, setShowCapabilityDropdown] = useState(false);

  // Sync prompt data when component loads or formData changes
  useEffect(() => {
    const currentPrompt = formData.generalPrompt || formData.llmConfig?.generalPrompt || '';
    if (currentPrompt && (!formData.generalPrompt || !formData.llmConfig?.generalPrompt)) {
      // Ensure prompt is available in both locations
      onFormDataChange({
        ...formData,
        generalPrompt: currentPrompt,
        llmConfig: {
          ...formData.llmConfig,
          generalPrompt: currentPrompt
        }
      });
    }
  }, [formData.generalPrompt, formData.llmConfig?.generalPrompt, formData, onFormDataChange]);

  // Sync configuredActions when formData changes
  useEffect(() => {
    const actions = formData.configuredActions;
    if (actions && typeof actions === 'object') {
      setConfiguredActions(actions);
    }
  }, [formData.configuredActions]);

  const handleAddCapability = (capabilityId) => {
    // Add the capability to configured actions with default config
    const capability = AVAILABLE_CAPABILITIES.find(cap => cap.id === capabilityId);
    if (capability) {
      // Generate unique key for the configured action
      let uniqueKey = capabilityId;
      let counter = 1;
      
      // Check if this capability already exists, and if so, create a unique key
      while (Object.keys(configuredActions).includes(uniqueKey)) {
        uniqueKey = `${capabilityId}_${counter}`;
        counter++;
      }
      
      // Generate sanitized name for the tool (used by Retell API)
      const sanitizedName = capability.name.toLowerCase()
        .replace(/[^a-z0-9\s_-]/g, '') // Remove any characters that aren't allowed in tool names
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/[_-]+/g, '_') // Replace multiple underscores/dashes with single underscore
        .replace(/^[_-]+|[_-]+$/g, ''); // Remove leading/trailing underscores/dashes
      
      const newConfiguredActions = {
        ...configuredActions,
        [uniqueKey]: {
          enabled: true,
          config: {
            name: sanitizedName, // Use sanitized name for Retell API
            description: capability.description,
            capabilityType: capabilityId // Store the original capability type
          }
        }
      };
      
      setConfiguredActions(newConfiguredActions);
      onFormDataChange({
        ...formData,
        configuredActions: newConfiguredActions
      });
      
      // Open the configuration modal with the capability type and track the unique key
      handleOpenToolModal(capabilityId, uniqueKey);
    }
    
    setShowCapabilityDropdown(false);
  };

  const handleRemoveCapability = (capabilityId) => {
    const newConfiguredActions = { ...configuredActions };
    delete newConfiguredActions[capabilityId];
    
    setConfiguredActions(newConfiguredActions);
    onFormDataChange({
      ...formData,
      configuredActions: newConfiguredActions
    });
  };


  const handleOpenToolModal = (toolType, uniqueKey = null) => {
    // If uniqueKey is provided, use it; otherwise, toolType is the unique key
    const key = uniqueKey || toolType;
    const existingConfig = configuredActions[key]?.config;
    const actualToolType = existingConfig?.capabilityType || toolType;
    
    setEditingTool(actualToolType);
    setEditingToolKey(key);
    setShowToolModal(true);
  };

  const handleSaveToolConfig = (toolType, config) => {
    // Use the tracked unique key for saving
    const key = editingToolKey || toolType;
    
    // Ensure capabilityType is preserved - this is critical for proper functionality
    if (!config.capabilityType) {
      config.capabilityType = toolType;
    }
    
    // Ensure the name in config is sanitized for Retell API
    if (!config.name || config.name === key) {
      const capability = AVAILABLE_CAPABILITIES.find(cap => cap.id === config.capabilityType);
      if (capability) {
        config.name = capability.name.toLowerCase()
          .replace(/[^a-z0-9\s_-]/g, '') // Remove any characters that aren't allowed in tool names
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .replace(/[_-]+/g, '_') // Replace multiple underscores/dashes with single underscore
          .replace(/^[_-]+|[_-]+$/g, ''); // Remove leading/trailing underscores/dashes
      }
    }
    
    const newConfiguredActions = {
      ...configuredActions,
      [key]: {
        enabled: true,
        config: config
      }
    };
    
    setConfiguredActions(newConfiguredActions);
    onFormDataChange({
      ...formData,
      configuredActions: newConfiguredActions
    });
  };

  const getConfiguredCapabilities = () => {
    // Safety check for configuredActions
    if (!configuredActions || typeof configuredActions !== 'object') {
      return [];
    }
    
    return Object.keys(configuredActions)
      .map(capabilityId => {
        const config = configuredActions[capabilityId]?.config;
        const capabilityType = config?.capabilityType || capabilityId;
        const capability = AVAILABLE_CAPABILITIES.find(cap => cap.id === capabilityType);
        
        // Handle case where capability is not found
        if (!capability) {
          console.warn(`Capability type '${capabilityType}' not found in AVAILABLE_CAPABILITIES`);
          return {
            id: capabilityId,
            name: `Unknown Capability (${capabilityId})`,
            description: 'Unknown capability type',
            icon: <DataObject />,
            config: configuredActions[capabilityId]?.config
          };
        }
        
        // Create a descriptive display name based on the configuration
        let displayName = capability.name;
        
        if (capabilityType === 'transfer_call') {
          if (config?.transfer_destination?.type === 'predefined') {
            displayName = `Call Transfer (${config.transfer_destination.number})`;
          } else if (config?.transfer_destination?.type === 'inferred') {
            displayName = 'Call Transfer (Dynamic)';
          } else {
            displayName = `Call Transfer (${capabilityId})`;
          }
        } else if (capabilityType === 'press_digit') {
          if (config?.delay_ms) {
            displayName = `Press Digit (${config.delay_ms}ms)`;
          } else {
            displayName = `Press Digit (${capabilityId})`;
          }
        } else if (capabilityType === 'extract_dynamic_variable') {
          if (config?.variables?.length > 0) {
            const varNames = config.variables.map(v => v.name).join(', ');
            displayName = `Extract Variables (${varNames})`;
          } else {
            displayName = `Extract Variables (${capabilityId})`;
          }
        } else if (capabilityType === 'agent_swap') {
          if (config?.target_agent_id) {
            displayName = `Agent Swap (${config.target_agent_id})`;
          } else {
            displayName = `Agent Swap (${capabilityId})`;
          }
        } else {
          // For other capabilities, add a number if it's not the first instance
          if (capabilityId !== capabilityType) {
            displayName = `${capability.name} (${capabilityId})`;
          }
        }
        
        return {
          ...capability,
          id: capabilityId, // Use the actual key as the ID
          name: displayName, // Use the descriptive name
          config: configuredActions[capabilityId]?.config
        };
      })
      .filter(capability => capability !== null); // Filter out any null entries
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Agent goals
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Set up Agent's personality and actions.
        </Typography>
        <Button 
          variant="outlined" 
          size="small" 
          sx={{ mt: 1 }}
        >
          Switch to basic mode
        </Button>
      </Box>

      {/* Knowledge Base Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select knowledge base
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select knowledge base for this agent</InputLabel>
            <Select
              value={formData.knowledgeBaseId || ''}
              onChange={(e) => onKnowledgeBaseChange(e.target.value)}
              label="Select knowledge base for this agent"
            >
              <MenuItem value="">
                <em>No knowledge base selected</em>
              </MenuItem>
              {knowledgeBases.map((kb) => (
                <MenuItem key={kb.id} value={kb.id}>
                  {kb.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
          >
            Create New
          </Button>
        </CardContent>
      </Card>

      {/* Welcome Message Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Welcome Message
          </Typography>
          
          {/* Welcome Message Configuration */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Welcome Message</InputLabel>
              <Select
                value={formData.welcomeMessageType || 'user_initiate'}
                onChange={(e) => onFormDataChange({
                  ...formData,
                  welcomeMessageType: e.target.value
                })}
                label="Welcome Message"
              >
                <MenuItem value="user_initiate">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <VolumeUp sx={{ mr: 1, fontSize: 20 }} />
                    User Initiates: AI remains silent until users speak first
                  </Box>
                </MenuItem>
                <MenuItem value="ai_dynamic">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chat sx={{ mr: 1, fontSize: 20 }} />
                    AI Initiates: AI begins with dynamic message
                  </Box>
                </MenuItem>
                <MenuItem value="ai_static">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chat sx={{ mr: 1, fontSize: 20 }} />
                    AI Initiates: AI begins with static message
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* AI Starts Speaking After Silence (only for user_initiate) */}
          {formData.welcomeMessageType === 'user_initiate' && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                  AI starts speaking after silence
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.beginAfterUserSilence !== false}
                      onChange={(e) => onFormDataChange({
                        ...formData,
                        beginAfterUserSilence: e.target.checked
                      })}
                    />
                  }
                  label=""
                />
              </Box>
              
              {formData.beginAfterUserSilence !== false && (
                <Box sx={{ pl: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Schedule sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      Silence Time:
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={formData.beginAfterUserSilenceMs || 5000}
                      onChange={(e) => onFormDataChange({
                        ...formData,
                        beginAfterUserSilenceMs: parseInt(e.target.value) || 0
                      })}
                      InputProps={{
                        endAdornment: <Typography variant="body2">ms</Typography>
                      }}
                      sx={{ width: 120, mr: 1 }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Welcome Message Text (only for ai_static) */}
          {formData.welcomeMessageType === 'ai_static' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Welcome Message Text
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={formData.welcomeMessage || 'Hello, how can I help you today?'}
                onChange={(e) => onFormDataChange({
                  ...formData,
                  welcomeMessage: e.target.value
                })}
                placeholder="Enter your welcome message..."
                helperText="Use {{}} to add variables. (Learn more)"
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Schedule sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ mr: 1 }}>
                  Pause Before Speaking:
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={formData.pauseBeforeSpeaking || 0}
                  onChange={(e) => onFormDataChange({
                    ...formData,
                    pauseBeforeSpeaking: parseInt(e.target.value) || 0
                  })}
                  InputProps={{
                    endAdornment: <Typography variant="body2">s</Typography>
                  }}
                  sx={{ width: 80 }}
                />
              </Box>
            </Box>
          )}


        </CardContent>
      </Card>

      {/* Prompt Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Prompt
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={formData.generalPrompt || formData.llmConfig?.generalPrompt || ''}
            onChange={(e) => {
              const newPrompt = e.target.value;
              onFormDataChange({
                ...formData,
                generalPrompt: newPrompt,
                llmConfig: {
                  ...formData.llmConfig,
                  generalPrompt: newPrompt
                }
              });
            }}
            placeholder="Define the agent's personality and behavior..."
            helperText="A good prompt will allow the agent to better interpret and respond appropriately."
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {2000 - ((formData.generalPrompt || formData.llmConfig?.generalPrompt || '').length)} words left
            </Typography>
            <Button 
              variant="text" 
              size="small"
              startIcon={<AddIcon />}
            >
              Prompt Guidelines
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Capabilities Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Capabilities
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowCapabilityDropdown(!showCapabilityDropdown)}
            >
              + Add
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enable your agent with capabilities such as calendar booking, call transfers, and data extraction.
          </Typography>

          {/* Capability Dropdown */}
          {showCapabilityDropdown && (
            <Paper sx={{ p: 2, mb: 2, maxHeight: 300, overflow: 'auto' }}>
              {AVAILABLE_CAPABILITIES.map((capability) => (
                <Box
                  key={capability.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    borderRadius: 1
                  }}
                  onClick={() => handleAddCapability(capability.id)}
                >
                  <Box sx={{ mr: 2, color: 'primary.main' }}>
                    {capability.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">
                      {capability.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {capability.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          )}

          {/* Configured Capabilities */}
          {Object.keys(configuredActions).length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Configured Capabilities:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {getConfiguredCapabilities().map((capability) => (
                  <Chip
                    key={capability.id}
                    icon={capability.icon}
                    label={capability.name}
                    onDelete={() => handleRemoveCapability(capability.id)}
                    onClick={() => handleOpenToolModal(capability.id)}
                    color="primary"
                    variant="outlined"
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>



      {/* Tool Configuration Modal */}
      <ToolConfigurationModal
        open={showToolModal}
        onClose={() => setShowToolModal(false)}
        toolType={editingTool}
        initialConfig={editingToolKey ? configuredActions[editingToolKey]?.config || {} : {}}
        onSave={handleSaveToolConfig}
      />
    </Box>
  );
};

export default AgentGoals; 